import type { Arrow, GridPoint, Direction, Level } from './types';

// ── Helpers ───────────────────────────────────────────────────────────────────

function key(p: GridPoint): string { return `${p.col},${p.row}`; }

export function directionDelta(dir: Direction): { dc: number; dr: number } {
  switch (dir) {
    case 'right': return { dc:  1, dr:  0 };
    case 'left':  return { dc: -1, dr:  0 };
    case 'down':  return { dc:  0, dr:  1 };
    case 'up':    return { dc:  0, dr: -1 };
  }
}

// ── Cell expansion ────────────────────────────────────────────────────────────

/**
 * Expand corner points into every individual cell the arrow occupies,
 * in order from tail (points[0]) to head (points[last]).
 */
export function expandArrow(arrow: Arrow): GridPoint[] {
  if (arrow.points.length === 0) return [];
  if (arrow.points.length === 1) return [{ ...arrow.points[0] }];

  const cells: GridPoint[] = [];
  for (let i = 0; i < arrow.points.length - 1; i++) {
    const a = arrow.points[i];
    const b = arrow.points[i + 1];
    if (a.col === b.col) {
      const step = b.row > a.row ? 1 : -1;
      for (let r = a.row; r !== b.row; r += step) cells.push({ col: a.col, row: r });
    } else {
      const step = b.col > a.col ? 1 : -1;
      for (let c = a.col; c !== b.col; c += step) cells.push({ col: c, row: a.row });
    }
  }
  cells.push({ ...arrow.points[arrow.points.length - 1] });
  return cells;
}

/**
 * Build the full animation path: original cells + `extra` cells extending
 * beyond the head in the arrow's direction (for exit animation).
 */
export function buildAnimPath(arrow: Arrow, extra: number): GridPoint[] {
  const cells = expandArrow(arrow);
  const { dc, dr } = directionDelta(arrow.direction);
  const last = cells[cells.length - 1];
  for (let i = 1; i <= extra; i++) {
    cells.push({ col: last.col + dc * i, row: last.row + dr * i });
  }
  return cells;
}

// ── Occupied cells (for collision detection with other arrows) ─────────────────

export function getOccupiedCells(arrow: Arrow): Set<string> {
  const s = new Set<string>();
  for (const c of expandArrow(arrow)) s.add(key(c));
  return s;
}

// ── Movement calculation ───────────────────────────────────────────────────────

export interface MoveResult {
  canExit: boolean;
  /** Steps the HEAD advances before hitting a blocker or leaving the grid */
  steps: number;
}

/**
 * Snake mechanic: only the HEAD advances into new territory.
 * The tail vacates cells — no need to re-check the body.
 */
export function calcMove(
  arrow: Arrow,
  otherArrows: Arrow[],
  level: Level
): MoveResult {
  const cells = expandArrow(arrow);
  const { dc, dr } = directionDelta(arrow.direction);
  const { cols, rows } = level;

  const blocked = new Set<string>();
  for (const other of otherArrows) {
    for (const c of getOccupiedCells(other)) blocked.add(c);
  }

  let headCell = { ...cells[cells.length - 1] };

  for (let steps = 0; steps < cols + rows + 20; steps++) {
    const next = { col: headCell.col + dc, row: headCell.row + dr };

    if (blocked.has(key(next))) {
      return { canExit: false, steps };
    }

    headCell = next;

    if (headCell.col < 0 || headCell.col >= cols || headCell.row < 0 || headCell.row >= rows) {
      return { canExit: true, steps: steps + 1 };
    }
  }

  return { canExit: true, steps: cols + rows };
}
